import BuildingIcon from "@redotech/redo-icons/building-02.svg";
import ClockIcon from "@redotech/redo-icons/clock-fast-forward.svg";
import FlipBackwardIcon from "@redotech/redo-icons/flip-backward.svg";
import PackageCheckIcon from "@redotech/redo-icons/package-check.svg";
import PackageSearchIcon from "@redotech/redo-icons/package-search.svg";
import RocketIcon from "@redotech/redo-icons/rocket-02.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import type { ComponentType } from "react";
import { memo } from "react";
import type {
  FormState,
  ProductHandling,
  ReturnsManagement,
  StepId,
} from "./managed-returns-trial-modal";
import * as styles from "./managed-returns-trial-modal.module.css";
import {
  Bullets,
  CheckList,
  NoteCard,
  ProcessFlow,
  SelectableCard,
  StepHeading,
  SubHeading,
} from "./managed-returns-trial-sections";

export const StepContent = memo(function StepContent({
  averageProcessingTimeLabel,
  stepId,
  isAutoProcessing,
  form,
  onSelectReturnsManagement,
  onSelectProductHandling,
}: {
  averageProcessingTimeLabel: string | null;
  stepId: StepId;
  isAutoProcessing: boolean;
  form: FormState;
  onSelectReturnsManagement: (value: ReturnsManagement) => void;
  onSelectProductHandling: (value: ProductHandling) => void;
}) {
  switch (stepId) {
    case "hook":
      return (
        <HookPage
          averageProcessingTimeLabel={averageProcessingTimeLabel}
          isAutoProcessing={isAutoProcessing}
        />
      );
    case "manage-today":
      return (
        <ManageTodayPage
          onSelect={onSelectReturnsManagement}
          selected={form.returnsManagement}
        />
      );
    case "processing":
      return (
        <ProcessingPage
          onSelect={onSelectProductHandling}
          selected={form.productHandling}
        />
      );
    case "refulfillment":
      return <RefulfillmentPage />;
    case "consolidation":
      return <ConsolidationPage />;
    case "confirmation":
      return <ConfirmationPage />;
    default:
      assertNever(stepId);
  }
});

const HookPage = memo(function HookPage({
  averageProcessingTimeLabel,
  isAutoProcessing,
}: {
  averageProcessingTimeLabel: string | null;
  isAutoProcessing: boolean;
}) {
  return (
    <Flex dir="column" gap="3xl">
      {isAutoProcessing ? (
        <StepHeading
          intro="Redo inspects each return before issuing a refund, then gets sellable inventory back on the shelf fast."
          title="You refund before anyone inspects. Let's fix that."
        />
      ) : (
        <StepHeading
          intro="Redo receives and processes your returns for you, then gets sellable inventory back on the shelf fast."
          title={
            averageProcessingTimeLabel
              ? `Your returns take ${averageProcessingTimeLabel} to process`
              : "Your returns can take days to process"
          }
        />
      )}
      <Flex dir="column" gap="xl">
        <HookBenefit
          description={
            isAutoProcessing
              ? "Verify condition before money leaves your business."
              : "Every item is inspected and prepared for resale."
          }
          Icon={PackageSearchIcon}
          title={
            isAutoProcessing ? "Inspect before refund" : "Inspect and refurbish"
          }
        />
        <HookBenefit
          description="Dock to stock in under three days."
          Icon={ClockIcon}
          title="Back in stock quickly"
        />
        <HookBenefit
          description="Your first 30 days are free."
          Icon={RocketIcon}
          title="Try it with no upfront cost"
        />
      </Flex>
    </Flex>
  );
});

const HookBenefit = memo(function HookBenefit({
  description,
  Icon,
  title,
}: {
  description: string;
  Icon: ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <Flex align="flex-start" gap="lg">
      <RedoFeaturedIcon
        color="brand"
        Icon={Icon}
        shape="circle"
        size="sm"
        type="default"
      />
      <Flex dir="column" gap="xxs">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {title}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          {description}
        </Text>
      </Flex>
    </Flex>
  );
});

const ManageTodayPage = memo(function ManageTodayPage({
  onSelect,
  selected,
}: {
  onSelect: (value: ReturnsManagement) => void;
  selected: ReturnsManagement | null;
}) {
  return (
    <Flex dir="column" gap="3xl">
      <StepHeading
        eyebrow="Step 1 of 3"
        intro="This helps us understand your setup. It won't change anything about your account."
        title="How do you manage your returns today?"
      />
      <Flex dir="column" gap="xl">
        <SelectableCard
          description="A third-party logistics provider receives and processes your returns."
          onSelect={() => onSelect("3pl")}
          selected={selected === "3pl"}
          title="With a 3PL"
        />
        <SelectableCard
          description="Your own team receives and processes returns in-house."
          onSelect={() => onSelect("in-house")}
          selected={selected === "in-house"}
          title="In-house"
        />
      </Flex>
    </Flex>
  );
});

const ProcessingPage = memo(function ProcessingPage({
  onSelect,
  selected,
}: {
  onSelect: (value: ProductHandling) => void;
  selected: ProductHandling | null;
}) {
  return (
    <Flex dir="column" gap="3xl">
      <StepHeading
        eyebrow="Step 2 of 3"
        intro="Returns come to a Redo processing center, get inspected and refurbished to back-to-stock condition, and are ready to sell again in under three days."
        title="How returns processing works"
      />
      <Flex dir="column" gap="xl">
        <ProcessFlow
          steps={[
            {
              icon: FlipBackwardIcon,
              label: "Customer returns",
              sub: "Shipped back",
            },
            {
              icon: BuildingIcon,
              label: "Redo receives",
              sub: "At our center",
            },
            {
              icon: PackageSearchIcon,
              label: "Inspect & refurbish",
              sub: "Resale-ready",
            },
            { icon: ClockIcon, label: "Back to stock", sub: "Under 3 days" },
          ]}
        />
      </Flex>
      <Flex dir="column" gap="xl">
        <SubHeading>What would you like us to do with the product?</SubHeading>
        <Flex className={styles.productChoices} gap="lg">
          <SelectableCard
            badge="Recommended"
            description="Ship returned inventory directly to the next customer."
            onSelect={() => onSelect("fulfill")}
            selected={selected === "fulfill"}
            title="Fulfill future orders"
          />
          <SelectableCard
            description="Send resale-ready returns back to your warehouse."
            onSelect={() => onSelect("consolidate")}
            selected={selected === "consolidate"}
            title="Return to your warehouse"
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

const RefulfillmentPage = memo(function RefulfillmentPage() {
  return (
    <Flex dir="column" gap="3xl">
      <StepHeading
        eyebrow="Step 3 of 3"
        intro="You chose to fulfill returns for the next customer. Here's how we get returned items back into inventory fast."
        title="How refulfillment works"
      />
      <Flex dir="column" gap="xl">
        <ProcessFlow
          steps={[
            {
              icon: PackageSearchIcon,
              label: "Inspect & grade",
              sub: "Confirm resellable",
            },
            {
              icon: PackageCheckIcon,
              label: "Back to stock",
              sub: "Refurbished condition",
            },
            {
              icon: RocketIcon,
              label: "Refulfill",
              sub: "Ships to next customer",
            },
          ]}
        />
      </Flex>
      <NoteCard
        icon={RocketIcon}
        title="A mini fulfillment center for your returns"
      >
        <p className={styles.noteBodyText}>
          We set up a mini fulfillment operation for your returned products. As
          soon as an item passes inspection, it's put back into sellable
          inventory and shipped directly to the next customer who orders it — no
          round-trip to your warehouse required.
        </p>
      </NoteCard>
      <Flex dir="column" gap="xl">
        <SubHeading>Why merchants choose this</SubHeading>
        <CheckList
          items={[
            "Returned inventory earns revenue again in days, not weeks",
            "No warehouse round-trip, so items sell before they go stale",
            "Less capital tied up in returns sitting in transit",
          ]}
        />
      </Flex>
    </Flex>
  );
});

const ConsolidationPage = memo(function ConsolidationPage() {
  return (
    <Flex dir="column" gap="3xl">
      <StepHeading
        eyebrow="Step 3 of 3"
        intro="You chose to consolidate returns back to your warehouse. Here's how we get items ready to sell before they ship to you."
        title="How consolidation works"
      />
      <Flex dir="column" gap="xl">
        <ProcessFlow
          steps={[
            {
              icon: PackageSearchIcon,
              label: "Inspect & refurbish",
              sub: "Back-to-stock condition",
            },
            {
              icon: BuildingIcon,
              label: "Consolidate",
              sub: "Batched for shipment",
            },
            { icon: TruckIcon, label: "Ship to you", sub: "Ready to sell" },
          ]}
        />
      </Flex>
      <NoteCard
        icon={TruckIcon}
        title="Sellable inventory, delivered to your warehouse"
      >
        <p className={styles.noteBodyText}>
          We receive, inspect, and refurbish your returns to back-to-stock
          condition, then consolidate them and ship them to your warehouse in
          batches. Everything arrives ready to sell, so your team doesn't spend
          time processing returns.
        </p>
      </NoteCard>
      <Flex dir="column" gap="xl">
        <SubHeading>Why merchants choose this</SubHeading>
        <Bullets
          items={[
            "Keep returned inventory in your own warehouse and systems",
            "Items arrive already inspected and back-to-stock ready",
            "Fewer, batched shipments instead of one-off return handling",
          ]}
        />
      </Flex>
    </Flex>
  );
});

const ConfirmationPage = memo(function ConfirmationPage() {
  return (
    <Flex dir="column" gap="3xl">
      <StepHeading
        intro="A Redo returns specialist will reach out within 24–48 hours to tailor your managed returns setup to how you work today. Your first 30 days are free, with no commitment."
        title="You're all set — we'll be in touch"
      />
      <p className={styles.finalNote}>
        Have questions before then? Reply to our outreach email and we'll walk
        you through everything.
      </p>
    </Flex>
  );
});
